import { useMemo } from "react";
import { useApiUrl, useCustomMutation } from "@refinedev/core";
import type { AddOnType, MediaProjectResponse } from "pages/media/types";

type OutOfDateAddOns = { storyboardSettingsId: string; types: AddOnType[] };

/**
 * Regenerates everything that is out of date on an experience: every
 * `OutOfDate` add-on of the project and, when the media is stale, the video.
 * Both run together; the action only counts as a success if all of them
 * succeed. Shared by ExperiencePage and the Edit page so "Regenerate" means
 * the same on both.
 */
export const useRegenerateOutOfDateItems = ({
  projectId,
  project,
  isMediaOutOfDate,
  regenerateOrApprove,
  invalidateProject,
}: {
  projectId: string | undefined;
  project: MediaProjectResponse | undefined;
  isMediaOutOfDate: boolean;
  regenerateOrApprove: () => Promise<boolean>;
  invalidateProject: () => Promise<void>;
}) => {
  const apiUrl = useApiUrl();
  const { mutateAsync, isPending } = useCustomMutation();

  // `add-ons/generate` is addressed per storyboard, so group by it.
  const outOfDateAddOns = useMemo<OutOfDateAddOns[]>(
    () =>
      (project?.storyboards ?? [])
        .map((storyboard) => ({
          storyboardSettingsId: storyboard.id,
          types: (storyboard.add_ons ?? [])
            .filter((addOn) => addOn.status === "OutOfDate")
            .map((addOn) => addOn.add_on_type),
        }))
        .filter((group) => group.types.length > 0),
    [project?.storyboards]
  );
  const isAddOnsOutOfDate = outOfDateAddOns.length > 0;

  const regenerateOutOfDateItems = async (): Promise<boolean> => {
    if (!projectId) return false;
    const calls: Promise<unknown>[] = outOfDateAddOns.map((group) =>
      mutateAsync({
        url: `${apiUrl}/media/projects/${projectId}/add-ons/generate`,
        method: "post",
        values: {
          add_on_types: group.types,
          storyboard_settings_id: group.storyboardSettingsId,
        },
        successNotification: false,
      })
    );
    if (isMediaOutOfDate) {
      // regenerateOrApprove swallows its errors and resolves false instead.
      calls.push(
        regenerateOrApprove().then((ok) => {
          if (!ok) throw new Error("Media regeneration failed");
        })
      );
    }
    const results = await Promise.allSettled(calls);
    await invalidateProject();
    return results.every((result) => result.status === "fulfilled");
  };

  return {
    isAddOnsOutOfDate,
    regenerateOutOfDateItems,
    isRegeneratingAddOns: isPending,
  };
};
